import { formatCommitReference } from "./format-commit-reference";
import { escapeMarkdownText } from "./escape-markdown-text";
import type {
  ReleaseConfiguration,
  TimelineCommit,
  TimelineEntry,
} from "@insights/contracts";

export type ReleaseNotesCategory = {
  name: string;
  emoji: string;
  color: string;
  aliases?: string[];
};

const shieldColorPalette: Record<string, string> = {
  lightgrey: "f1f3f5",
  gray: "e9ecef",
  red: "ffc9c9",
  orange: "ffd8a8",
  yellow: "fff3bf",
  green: "b2f2bb",
  teal: "96f2d7",
  blue: "a5d8ff",
  indigo: "bac8ff",
  pink: "ffdeeb",
};

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseCommitSubject(
  subject: string,
  configuration: ReleaseConfiguration,
) {
  let remainingText = subject.trim();
  let subjectMarksRemoval = false;
  if (remainingText.startsWith("Removed: ")) {
    subjectMarksRemoval = true;
    remainingText = remainingText.slice("Removed: ".length).trimStart();
  }

  for (const category of configuration.releaseNotesCategories) {
    for (const alias of category.aliases) {
      const match = remainingText.match(
        new RegExp(`^${escapeRegex(alias)}\\s*:\\s*`, "i"),
      );
      if (match) {
        return {
          category,
          subject: remainingText.slice(match[0].length),
          subjectMarksRemoval,
        };
      }
    }
  }

  const conventionalPrefix = remainingText.match(/^([^\s:]+)\s*:\s*(.+)$/);
  return {
    category: configuration.releaseNotesFallbackCategory,
    subject: conventionalPrefix?.[2] ?? remainingText,
    subjectMarksRemoval,
  };
}

function shieldBadgeUrl(category: ReleaseNotesCategory, sha: string) {
  const color = shieldColorPalette[category.color] ?? category.color;

  return `https://img.shields.io/badge/${encodeURIComponent(category.name)}-${encodeURIComponent(formatCommitReference(sha))}-gray?style=flat&labelColor=${encodeURIComponent(color)}`;
}

export function releaseNotesCommitPresentation(
  commit: TimelineCommit,
  removed: boolean,
  configuration: ReleaseConfiguration,
) {
  const subject = commit.message.split("\n")[0]?.trim() ?? "";
  const pullRequest = subject.match(/\(#(\d+)\)$/);
  const subjectWithoutPullRequest = pullRequest
    ? subject.replace(/\s*\(#\d+\)$/, "")
    : subject;
  const parsed = parseCommitSubject(subjectWithoutPullRequest, configuration);

  return {
    category: parsed.category,
    pullRequest: pullRequest?.[1] ?? null,
    removed: removed || parsed.subjectMarksRemoval,
    subject: parsed.subject,
  };
}

export function formatReleaseNotesCommitLine(
  repository: string,
  commit: TimelineCommit,
  removed: boolean,
  configuration: ReleaseConfiguration,
) {
  const presentation = releaseNotesCommitPresentation(
    commit,
    removed,
    configuration,
  );
  const removalPrefix = presentation.removed ? "Removed: " : "";
  const pullRequestLink = presentation.pullRequest
    ? ` [#${presentation.pullRequest}](https://github.com/${repository}/pull/${presentation.pullRequest})`
    : "";
  const commitUrl = `https://github.com/${repository}/commit/${commit.sha}`;
  const shortSha = formatCommitReference(commit.sha);

  if (configuration.releaseNotesMarkerMode === "shield") {
    const badge = `[![${escapeMarkdownText(presentation.category.name)}-${shortSha}](${shieldBadgeUrl(presentation.category, commit.sha)})](${commitUrl})`;

    return `- ${removalPrefix}${badge} ${escapeMarkdownText(presentation.subject)}${pullRequestLink}`;
  }

  return `- ${removalPrefix}${presentation.category.emoji} [${shortSha}](${commitUrl}) ${escapeMarkdownText(presentation.subject)}${pullRequestLink}`;
}

export function buildTimelineReleaseNotes(
  repository: string,
  environmentName: string,
  entry: TimelineEntry,
  configuration: ReleaseConfiguration,
  appliedTag?: string,
) {
  const commits = [
    ...entry.introducedCommits.map((commit) => ({ commit, removed: false })),
    ...entry.removedCommits.map((commit) => ({ commit, removed: true })),
  ].sort(
    (left, right) =>
      Date.parse(right.commit.timestamp) - Date.parse(left.commit.timestamp),
  );
  const header = [
    "# Release Notes",
    "",
    "**Context:** Timeline\\",
    `**Repository:** ${escapeMarkdownText(repository)}\\`,
    `**Environment:** ${escapeMarkdownText(environmentName)}\\`,
    ...(appliedTag
      ? [`**Applied Tag:** ${escapeMarkdownText(appliedTag)}\\`]
      : []),
    `**Entry Head:** ${escapeMarkdownText(formatCommitReference(entry.head.sha))}`,
    "",
    ...(!entry.historyComplete
      ? ["> Commit ancestry is incomplete for part of this transition.", ""]
      : []),
    "# Changes",
    "",
  ];
  const lines = commits.map(({ commit, removed }) =>
    formatReleaseNotesCommitLine(repository, commit, removed, configuration),
  );

  return [...header, ...lines].join("\n");
}
